import axios from 'axios'
import {
  commonHeadersMPWeb,
  getCountryCode,
  getMainLanguageCode,
  getMPDataWithParams,
  mentorApiURL,
  postMPDataWithoutParams,
} from 'src/mentor/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const BASE_PATH = '/api/secure/mentor/contact-actions'
const communicationTargetsCache = new Map()
const DEFAULT_TARGETS_FALLBACK_CONCURRENCY = 4

// Coalesces contact-capability look-ups: the shared Patient Quick Actions component can mount on
// several surfaces at once (session screen + inline report preview) and re-check across renders, so
// in-flight requests for the same context are shared and successful results reused for a short TTL.
const contactCapabilitiesCache = new Map()
const CONTACT_CAPABILITIES_TTL_MS = 30000

function buildContactCapabilitiesCacheKey(context = {}) {
  return [
    context?.bookedEventId || '',
    context?.patientId || '',
    context?.clinicBrandId || '',
    context?.clinicId || '',
  ].join('|')
}

export function clearMentorContactCapabilitiesCache() {
  contactCapabilitiesCache.clear()
}

function getResponseMessage(response, fallbackMessage) {
  return (
    response?.data?.message ||
    response?.response?.data?.message ||
    response?.message ||
    fallbackMessage
  )
}

function buildBookedEventPayload(bookedEventId) {
  return {
    bookedEventId: bookedEventId || undefined,
  }
}

function normalizeBookedEventIds(bookedEventIds = []) {
  return Array.from(
    new Set(
      (Array.isArray(bookedEventIds) ? bookedEventIds : [])
        .map((bookedEventId) => `${bookedEventId || ''}`.trim())
        .filter(Boolean),
    ),
  )
}

function buildSuccessResponse(response) {
  return {
    ok: true,
    data: getDataMapFromCommonResponse(response),
    rawResponse: response,
  }
}

function cacheCommunicationTargets(bookedEventId, targets, responseData = {}, rawResponse = null) {
  const success = {
    ok: true,
    data: responseData,
    rawResponse,
    targets: targets || {},
  }
  if (bookedEventId) {
    communicationTargetsCache.set(bookedEventId, success)
  }
  return success
}

async function fetchCommunicationTargetsIndividually(bookedEventIds = [], options = {}) {
  const normalizedBookedEventIds = normalizeBookedEventIds(bookedEventIds)
  const targetsByBookedEventId = {}
  const errorsByBookedEventId = {}
  const concurrency = Math.max(
    1,
    Number(options?.fallbackConcurrency) || DEFAULT_TARGETS_FALLBACK_CONCURRENCY,
  )

  for (let index = 0; index < normalizedBookedEventIds.length; index += concurrency) {
    const bookedEventIdsChunk = normalizedBookedEventIds.slice(index, index + concurrency)
    const chunkResults = await Promise.all(
      bookedEventIdsChunk.map(async (bookedEventId) => ({
        bookedEventId,
        result: await getMentorCommunicationTargets(bookedEventId, options),
      })),
    )

    chunkResults.forEach(({ bookedEventId, result }) => {
      if (result?.ok) {
        targetsByBookedEventId[bookedEventId] = result.targets || {}
        return
      }

      errorsByBookedEventId[bookedEventId] = {
        message: result?.message || 'Unable to load communication targets.',
      }
    })
  }

  return {
    targetsByBookedEventId,
    errorsByBookedEventId,
  }
}

export async function getMentorCommunicationTargets(bookedEventId, options = {}) {
  const cacheKey = bookedEventId || ''
  if (!options?.force && cacheKey && communicationTargetsCache.has(cacheKey)) {
    return communicationTargetsCache.get(cacheKey)
  }

  const response = await getMPDataWithParams(`${BASE_PATH}/targets`, buildBookedEventPayload(bookedEventId))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const responseData = getDataMapFromCommonResponse(response) || {}
    return cacheCommunicationTargets(cacheKey, responseData?.targets || {}, responseData, response)
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to load communication targets.'),
    rawResponse: response,
  }
}

export function clearMentorCommunicationTargetsCache(bookedEventId) {
  if (!bookedEventId) {
    communicationTargetsCache.clear()
    return
  }
  communicationTargetsCache.delete(bookedEventId)
}

export async function getMentorCommunicationTargetsBatch(bookedEventIds = [], options = {}) {
  const normalizedBookedEventIds = normalizeBookedEventIds(bookedEventIds)
  if (normalizedBookedEventIds.length === 0) {
    return {
      ok: true,
      targetsByBookedEventId: {},
      errorsByBookedEventId: {},
    }
  }

  const cachedTargetsByBookedEventId = {}
  const missingBookedEventIds = []

  normalizedBookedEventIds.forEach((bookedEventId) => {
    if (!options?.force && communicationTargetsCache.has(bookedEventId)) {
      cachedTargetsByBookedEventId[bookedEventId] = communicationTargetsCache.get(bookedEventId)?.targets || {}
      return
    }
    missingBookedEventIds.push(bookedEventId)
  })

  if (missingBookedEventIds.length === 0) {
    return {
      ok: true,
      targetsByBookedEventId: cachedTargetsByBookedEventId,
      errorsByBookedEventId: {},
    }
  }

  const response = await getMPDataWithParams(`${BASE_PATH}/targets/batch`, {
    bookedEventIds: missingBookedEventIds.join(','),
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const responseData = getDataMapFromCommonResponse(response) || {}
    const fetchedTargetsByBookedEventId = responseData?.targetsByBookedEventId || {}
    const batchErrorsByBookedEventId = responseData?.errorsByBookedEventId || {}

    Object.entries(fetchedTargetsByBookedEventId).forEach(([bookedEventId, targets]) => {
      cacheCommunicationTargets(bookedEventId, targets, { targets }, response)
    })

    const idsMissingFromBatch = missingBookedEventIds.filter(
      (bookedEventId) => !fetchedTargetsByBookedEventId?.[bookedEventId],
    )
    const shouldFallbackToSingles = idsMissingFromBatch.length > 0 && options?.disableFallback !== true
    const fallbackResult = shouldFallbackToSingles
      ? await fetchCommunicationTargetsIndividually(idsMissingFromBatch, options)
      : { targetsByBookedEventId: {}, errorsByBookedEventId: {} }

    return {
      ok: true,
      rawResponse: response,
      targetsByBookedEventId: {
        ...cachedTargetsByBookedEventId,
        ...fetchedTargetsByBookedEventId,
        ...fallbackResult.targetsByBookedEventId,
      },
      errorsByBookedEventId: {
        ...batchErrorsByBookedEventId,
        ...fallbackResult.errorsByBookedEventId,
      },
    }
  }

  if (options?.disableFallback !== true) {
    const fallbackResult = await fetchCommunicationTargetsIndividually(missingBookedEventIds, options)
    const mergedTargetsByBookedEventId = {
      ...cachedTargetsByBookedEventId,
      ...fallbackResult.targetsByBookedEventId,
    }

    return {
      ok: Object.keys(mergedTargetsByBookedEventId).length > 0,
      message: getResponseMessage(response, 'Unable to load communication targets.'),
      rawResponse: response,
      targetsByBookedEventId: mergedTargetsByBookedEventId,
      errorsByBookedEventId: fallbackResult.errorsByBookedEventId || {},
    }
  }

  return {
    ok: Object.keys(cachedTargetsByBookedEventId).length > 0,
    message: getResponseMessage(response, 'Unable to load communication targets.'),
    rawResponse: response,
    targetsByBookedEventId: cachedTargetsByBookedEventId,
    errorsByBookedEventId: {},
  }
}

export async function getMentorPatientContactCapabilities(bookedEventId) {
  const response = await getMPDataWithParams(`${BASE_PATH}/capabilities`, buildBookedEventPayload(bookedEventId))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const success = buildSuccessResponse(response)
    return {
      ...success,
      channels: success?.data?.channels || {},
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to load patient communication capabilities.'),
    rawResponse: response,
  }
}

export async function getMentorPatientWhatsappThread(bookedEventId) {
  const response = await getMPDataWithParams(`${BASE_PATH}/whatsapp/thread`, buildBookedEventPayload(bookedEventId))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const success = buildSuccessResponse(response)
    return {
      ...success,
      messages: success?.data?.messages || [],
      templateActions: success?.data?.templateActions || [],
      channels: success?.data?.channels || {},
      freeFormAllowed: !!success?.data?.freeFormAllowed,
      freeFormWindowOpen: !!success?.data?.freeFormWindowOpen,
      freeFormWindowStartAt: success?.data?.freeFormWindowStartAt,
      freeFormWindowEndAt: success?.data?.freeFormWindowEndAt,
      targetDisplayName: success?.data?.targetDisplayName || '',
      targetWhatsappNumber: success?.data?.targetWhatsappNumber || '',
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to load the WhatsApp conversation.'),
    rawResponse: response,
  }
}

export async function sendMentorPatientWhatsappTemplate(bookedEventId, templateAction = '') {
  const response = await postMPDataWithoutParams(`${BASE_PATH}/whatsapp/template`, {
    ...buildBookedEventPayload(bookedEventId),
    templateAction,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return buildSuccessResponse(response)
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the WhatsApp template.'),
    rawResponse: response,
  }
}

export async function sendMentorPatientWhatsappMessage(bookedEventId, message = '') {
  const response = await postMPDataWithoutParams(`${BASE_PATH}/whatsapp/message`, {
    ...buildBookedEventPayload(bookedEventId),
    message,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return buildSuccessResponse(response)
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the WhatsApp message.'),
    rawResponse: response,
  }
}

export async function sendMentorPatientWhatsappAttachment(bookedEventId, caption = '', files = []) {
  const token = localStorage.getItem('token')
  const countryCode = getCountryCode()
  const mainLanguageCode = getMainLanguageCode()
  const formData = new FormData()

  if (bookedEventId) {
    formData.append('bookedEventId', bookedEventId)
  }
  if (caption) {
    formData.append('caption', caption)
  }
  ;(files || []).forEach((file) => {
    if (file) {
      formData.append('files', file)
    }
  })

  try {
    const response = await axios.post(`${mentorApiURL}${BASE_PATH}/whatsapp/attachment`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...commonHeadersMPWeb,
        'ef-user-country-code': countryCode,
        'ef-app-language': mainLanguageCode,
      },
    })

    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      return buildSuccessResponse(response)
    }

    return {
      ok: false,
      message: getResponseMessage(response, 'Unable to send the WhatsApp attachment.'),
      rawResponse: response,
    }
  } catch (error) {
    return {
      ok: false,
      message: getResponseMessage(error, 'Unable to send the WhatsApp attachment.'),
      rawResponse: error,
    }
  }
}

// -----------------------------------------------------------------------------
// Context-capable variants used by the shared Patient Quick Actions component.
// These accept a `context` of either { bookedEventId } (session) or
// { patientId, clinicBrandId, clinicId } (patient drawer). The patient-keyed
// path requires the backend patientId-keying (PractitionerPatientContactService)
// to be deployed. Existing bookedEventId-only functions above are unchanged.
// -----------------------------------------------------------------------------

function buildContactContextPayload(context = {}) {
  if (context?.bookedEventId) {
    return { bookedEventId: context.bookedEventId }
  }
  return {
    patientId: context?.patientId || undefined,
    clinicBrandId: context?.clinicBrandId || undefined,
    clinicId: context?.clinicId || undefined,
  }
}

async function fetchMentorContactCapabilities(context = {}) {
  const response = await getMPDataWithParams(`${BASE_PATH}/capabilities`, buildContactContextPayload(context))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      data,
      channels: data?.channels || {},
      googleReviewConfigured: !!data?.googleReviewConfigured,
      patientHasAccount: !!data?.patientHasAccount,
      targetDisplayName: data?.targetDisplayName || '',
      targetWhatsappNumber: data?.targetWhatsappNumber || '',
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to load contact capabilities.'),
    rawResponse: response,
  }
}

export async function getMentorContactCapabilities(context = {}) {
  const cacheKey = buildContactCapabilitiesCacheKey(context)
  if (!cacheKey) {
    return fetchMentorContactCapabilities(context)
  }

  const cached = contactCapabilitiesCache.get(cacheKey)
  if (cached && (cached.pending || Date.now() - cached.at < CONTACT_CAPABILITIES_TTL_MS)) {
    return cached.promise
  }

  const entry = { pending: true, at: Date.now(), promise: null }
  entry.promise = (async () => {
    try {
      const result = await fetchMentorContactCapabilities(context)
      entry.pending = false
      entry.at = Date.now()
      // Don't cache failures — a transient error should self-heal on the next mount.
      if (!result?.ok) {
        contactCapabilitiesCache.delete(cacheKey)
      }
      return result
    } catch (error) {
      contactCapabilitiesCache.delete(cacheKey)
      throw error
    }
  })()
  contactCapabilitiesCache.set(cacheKey, entry)
  return entry.promise
}

// Live Twilio/Meta delivery status for the recent outbound WhatsApp messages to this patient.
// Backend sends are async + optimistic ("sent" only means handed to Twilio), so this is the only
// way the UI can show what actually happened (delivered / read / silently dropped by Meta / phone
// unreachable). Read-only.
export async function getMentorDeliverySummaryByContext(context = {}, { sinceHours, limit } = {}) {
  const response = await getMPDataWithParams(`${BASE_PATH}/delivery-summary`, {
    ...buildContactContextPayload(context),
    ...(sinceHours ? { sinceHours } : {}),
    ...(limit ? { limit } : {}),
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      whatsappAvailable: !!data?.whatsappAvailable,
      probeFailed: !!data?.probeFailed,
      patientWhatsappNumberMasked: data?.patientWhatsappNumberMasked || '',
      deliveries: Array.isArray(data?.deliveries) ? data.deliveries : [],
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to check delivery status.'),
    rawResponse: response,
  }
}

export async function sendMentorPrescriptionByContext(context = {}, deliveryMode) {
  const response = await postMPDataWithoutParams(`${BASE_PATH}/send-prescription`, {
    ...buildContactContextPayload(context),
    ...(deliveryMode ? { deliveryMode } : {}),
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return buildSuccessResponse(response)
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the prescription.'),
    rawResponse: response,
  }
}

export async function sendMentorPaymentLinkByContext(context = {}, amount, purpose = '') {
  const response = await postMPDataWithoutParams(`${BASE_PATH}/send-payment-link`, {
    ...buildContactContextPayload(context),
    amount,
    purpose,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      paymentLink: data?.paymentLink || '',
      amount: data?.amount,
      warmWindowOpen: !!data?.warmWindowOpen,
      whatsappSent: !!data?.whatsappSent,
      emailSent: !!data?.emailSent,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to create the payment link.'),
    rawResponse: response,
  }
}

export async function sendMentorGoogleReviewByContext(context = {}) {
  const response = await postMPDataWithoutParams(`${BASE_PATH}/send-google-review`, buildContactContextPayload(context))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      reviewLink: data?.reviewLink || '',
      whatsappSent: !!data?.whatsappSent,
      emailSent: !!data?.emailSent,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the Google review link.'),
    rawResponse: response,
  }
}

export async function createMentorReceiptByContext(context = {}, input = {}) {
  const response = await postMPDataWithoutParams(`${BASE_PATH}/bill-patient`, {
    ...buildContactContextPayload(context),
    ...input,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return { ok: true, ...data, rawResponse: response }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to create the receipt.'),
    rawResponse: response,
  }
}

export async function sendMentorReceipt(receiptId, channels = []) {
  const response = await postMPDataWithoutParams(`${BASE_PATH}/bill-patient/send`, { receiptId, channels })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return { ok: true, ...data, rawResponse: response }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the receipt.'),
    rawResponse: response,
  }
}

export async function fetchMentorReceiptPdfBlobUrl(receiptId) {
  const token = localStorage.getItem('token')
  const countryCode = getCountryCode()
  const mainLanguageCode = getMainLanguageCode()
  try {
    const response = await axios.get(`${mentorApiURL}${BASE_PATH}/bill-patient/pdf`, {
      params: { receiptId },
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`,
        ...commonHeadersMPWeb,
        'ef-user-country-code': countryCode,
        'ef-app-language': mainLanguageCode,
      },
    })
    if (response?.status === 200 && response?.data) {
      return { ok: true, blobUrl: URL.createObjectURL(response.data) }
    }
    return { ok: false, message: 'Unable to load the receipt PDF.' }
  } catch (error) {
    return { ok: false, message: 'Unable to load the receipt PDF.' }
  }
}

export async function getMentorPrescriptionsByContext(context = {}) {
  const response = await getMPDataWithParams(`${BASE_PATH}/prescriptions`, buildContactContextPayload(context))

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return { ok: true, prescriptions: data?.prescriptions || [], rawResponse: response }
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to load prescriptions.'),
    rawResponse: response,
  }
}

export async function sendMentorWhatsappTemplateByContext(context = {}, templateAction = '') {
  const response = await postMPDataWithoutParams(`${BASE_PATH}/whatsapp/template`, {
    ...buildContactContextPayload(context),
    templateAction,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return buildSuccessResponse(response)
  }

  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to send the WhatsApp template.'),
    rawResponse: response,
  }
}

export async function sendMentorWhatsappAttachmentByContext(context = {}, caption = '', files = []) {
  const token = localStorage.getItem('token')
  const countryCode = getCountryCode()
  const mainLanguageCode = getMainLanguageCode()
  const formData = new FormData()

  const payload = buildContactContextPayload(context)
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && `${value}` !== '') {
      formData.append(key, value)
    }
  })
  if (caption) {
    formData.append('caption', caption)
  }
  ;(files || []).forEach((file) => {
    if (file) {
      formData.append('files', file)
    }
  })

  try {
    const response = await axios.post(`${mentorApiURL}${BASE_PATH}/whatsapp/attachment`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...commonHeadersMPWeb,
        'ef-user-country-code': countryCode,
        'ef-app-language': mainLanguageCode,
      },
    })

    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      return buildSuccessResponse(response)
    }

    return {
      ok: false,
      message: getResponseMessage(response, 'Unable to send the WhatsApp attachment.'),
      rawResponse: response,
    }
  } catch (error) {
    return {
      ok: false,
      message: getResponseMessage(error, 'Unable to send the WhatsApp attachment.'),
      rawResponse: error,
    }
  }
}
